import { useMemo, useState } from 'react';
import type { GanttData, ProjectMeta } from '../types';
import { detectYear, tsvDateRange, tsvFullRow } from '../lib/tsv';
import { shortMonthDay } from '../lib/dates';

interface Props {
  meta: ProjectMeta;
  gantt: GanttData;
  groups: string[];
  tsvStart: string;
  tsvEnd: string;
  onTsvStartChange: (v: string) => void;
  onTsvEndChange: (v: string) => void;
}

type Mode = 'fullRow' | 'dateRange';

export function TsvOutput({ meta, gantt, groups, tsvStart, tsvEnd, onTsvStartChange, onTsvEndChange }: Props) {
  const [mode, setMode] = useState<Mode>('dateRange');
  const [copied, setCopied] = useState<string | null>(null);
  const year = detectYear(meta);

  const effectiveStart = tsvStart || meta.startDate;
  const effectiveEnd = tsvEnd || meta.endDate;

  const tsv = useMemo(() => {
    if (mode === 'fullRow') {
      return tsvFullRow(meta, gantt, groups, year);
    }
    if (!effectiveStart || !effectiveEnd) return '';
    return tsvDateRange(gantt, groups, effectiveStart, effectiveEnd);
  }, [mode, meta, gantt, groups, year, effectiveStart, effectiveEnd]);

  const cellCount = tsv ? tsv.split('\t').length : 0;

  const copy = async () => {
    if (!tsv) return;
    try {
      await navigator.clipboard.writeText(tsv);
      setCopied(mode);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = tsv;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(mode);
      setTimeout(() => setCopied(null), 1500);
    }
  };

  return (
    <div className="rounded-lg border border-tb-border bg-tb-surface p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-tb-text">輸出 TSV（複製貼回 Google Sheet）</h2>
        <div className="text-xs text-tb-text2">{cellCount} 格</div>
      </div>

      <div className="flex gap-1 mb-3 text-xs">
        <button
          onClick={() => setMode('dateRange')}
          className={[
            'px-3 py-1.5 rounded border transition',
            mode === 'dateRange' ? 'bg-brand-yellow text-brand-black border-brand-yellow font-semibold' : 'bg-tb-surface text-tb-text2 border-tb-border hover:bg-tb-bg',
          ].join(' ')}
        >
          📅 日期區段
        </button>
        <button
          onClick={() => setMode('fullRow')}
          className={[
            'px-3 py-1.5 rounded border transition',
            mode === 'fullRow' ? 'bg-brand-yellow text-brand-black border-brand-yellow font-semibold' : 'bg-tb-surface text-tb-text2 border-tb-border hover:bg-tb-bg',
          ].join(' ')}
        >
          📋 整列（含 metadata + {year} 全年）
        </button>
      </div>

      {mode === 'dateRange' && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tb-text2">起始日</span>
            <input type="date" className="input" value={effectiveStart} onChange={e => onTsvStartChange(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tb-text2">結束日</span>
            <input type="date" className="input" value={effectiveEnd} onChange={e => onTsvEndChange(e.target.value)} />
          </label>
        </div>
      )}

      <div className="bg-tb-bg border border-tb-border rounded p-2 mb-3 max-h-32 overflow-auto">
        <code className="text-[10px] text-tb-text whitespace-pre-wrap break-all">
          {tsv ? (tsv.length > 2000 ? tsv.slice(0, 2000) + ' …(已截斷預覽)' : tsv) : <span className="text-tb-text3">（無資料）</span>}
        </code>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-tb-text2">
          {mode === 'dateRange'
            ? `從 ${effectiveStart ? shortMonthDay(effectiveStart) : '?'} 起 ${cellCount} 格 → 貼到對應的日期欄`
            : `${year} 年完整列 → 貼到「${year}」分頁從 A 欄起的整列`}
        </p>
        <button
          onClick={copy}
          disabled={!tsv}
          className="px-4 py-1.5 text-xs rounded bg-brand-yellow text-brand-black font-semibold hover:bg-brand-yellow-dark disabled:bg-tb-border disabled:text-tb-text3"
        >
          {copied === mode ? '✓ 已複製' : '複製 TSV'}
        </button>
      </div>
    </div>
  );
}
